import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DependenciesModelBuilderService {
  constructor() {}

  insertInto(model: any, fieldPath: string): any {
    const path: string[] = fieldPath.split('.');
    model = this.initialiseModel(model, path);
    const state = { parent: null, entry: model };
    let pathIndex = 0;
    while (pathIndex < path.length) {
      if (typeof state.entry === 'string') {
        this.handleStringEntry(path, pathIndex, state);
      } else if (Array.isArray(state.entry)) {
        this.handleArrayEntry(path, pathIndex, state);
      } else {
        this.handleObjectMap(path, pathIndex, state);
      }

      pathIndex++;
    }
    return model;
  }

  getPaths(model: any): string[] {
    if (!model) {
      return [];
    }

    if (typeof model === 'string' || Array.isArray(model)) {
      throw new Error('Expected model to be a map.');
    }

    const pathList: string[] = [];

    const traverseModel = (key: string | null, value: any, currentPath: string): void => {
      const fullPath = key ? `${currentPath}${key}.` : currentPath;

      if (typeof value === 'string') {
        pathList.push(`${fullPath}${value}`);
      } else if (Array.isArray(value)) {
        value.forEach((item) => traverseModel(null, item, fullPath));
      } else if (typeof value === 'object' && value !== null) {
        Object.keys(value).forEach((subKey) => traverseModel(subKey, value[subKey], fullPath));
      }
    };

    Object.keys(model).forEach((key: string) => {
      traverseModel(key, model[key as keyof typeof model], '');
    });

    return pathList;
  }

  private initialiseModel(model: any, path: string[]): any {
    model = model ?? {};
    if (path.length > 0) {
      const root = path[0];
      if (!model[root]) {
        model[root] = {};
      }
    }
    return model;
  }

  private handleStringEntry(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    const ei = state.parent!.findIndex((f: any) => f === state.entry);
    state.parent!.splice(ei, 1); // remove the string entry in the parent / owner object
    const obj: any = [path[pathIndex]];
    state.parent!.push({ [state.entry]: obj }); // replace with an array (e.g. "documents": ["AttachmentName"])
    state.parent = obj;
    state.entry = obj[0];
  }

  private handleArrayEntry(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    // the array can contain plain strings or complete objects
    let item = state.entry.find((k: any) => {
      if (typeof k === 'string') {
        return k === path[pathIndex];
      }
      return Object.keys(k).includes(path[pathIndex]);
    });

    if (!item) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry.push({ [path[pathIndex]]: obj });
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      // if the item is an object then we want to use the value of that object pointed to by our path
      if (typeof item === 'object') {
        item = Object.entries(item as object).find(([k, v]) => k === path[pathIndex])![1];
      }
      state.parent = state.entry;
      state.entry = item;
    }
  }

  private handleObjectMap(path: string[], pathIndex: number, state: { entry: any; parent: any }): void {
    const entries = Object.entries(state.entry);

    let entry: any = entries.find(([k, v]) => k === path[pathIndex]);
    entry = entry ? entry[1] : undefined;

    if (!entry) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry[path[pathIndex]] = obj;
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      state.entry = entry;
    }
  }
}
